import { getNormalisedString } from '@actual-app/core/shared/normalisation';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryGroupEntity } from '@actual-app/core/types/models';

import { LISTO_PARA_ASIGNAR } from './cubrir';

/**
 * «Mover dinero» (H27): lógica pura de la hoja. Un extremo queda fijo (el
 * origen, o el destino si se invierte con ↑↓) y se elige el otro de una
 * lista; tocarlo mueve al momento.
 */

/** Categoría (o «Listo para asignar») que se puede tocar en la lista. */
export type CandidataMover = {
  /** Id de la categoría o `LISTO_PARA_ASIGNAR`. */
  id: string;
  /** Nombre de la categoría; `null` para «Listo para asignar». */
  nombre: string | null;
  /** Disponible este mes (puede ser negativo). */
  saldo: IntegerAmount;
  /** Disponible < 0: lleva la etiqueta «en rojo». */
  enRojo: boolean;
};

type Saldos = ReadonlyMap<string, { balance: IntegerAmount } | undefined>;

/**
 * Destinos posibles desde `origen`: primero las categorías en rojo, luego
 * las del grupo del origen y luego el resto (cada bloque en el orden del
 * Plan). «Listo para asignar» va al final (para devolver dinero) salvo que
 * sea el origen. Fuera: ingresos, ocultas (o de un grupo oculto), borradas
 * y el propio origen.
 */
export function destinosMover({
  grupos,
  saldos,
  toBudget,
  origen,
}: {
  grupos: readonly CategoryGroupEntity[];
  saldos: Saldos;
  toBudget: IntegerAmount | null | undefined;
  origen: string;
}): CandidataMover[] {
  const enRojo: CandidataMover[] = [];
  const delGrupo: CandidataMover[] = [];
  const resto: CandidataMover[] = [];
  for (const grupo of grupos) {
    if (grupo.is_income || grupo.hidden || grupo.tombstone) {
      continue;
    }
    const mismoGrupo = (grupo.categories ?? []).some(c => c.id === origen);
    for (const c of grupo.categories ?? []) {
      if (c.id === origen || c.is_income || c.hidden || c.tombstone) {
        continue;
      }
      const saldo = saldos.get(c.id)?.balance ?? 0;
      const fila = { id: c.id, nombre: c.name, saldo, enRojo: saldo < 0 };
      (fila.enRojo ? enRojo : mismoGrupo ? delGrupo : resto).push(fila);
    }
  }
  const lista = [...enRojo, ...delGrupo, ...resto];
  return origen === LISTO_PARA_ASIGNAR
    ? lista
    : [
        ...lista,
        {
          id: LISTO_PARA_ASIGNAR,
          nombre: null,
          saldo: toBudget ?? 0,
          enRojo: (toBudget ?? 0) < 0,
        },
      ];
}

/**
 * Orígenes posibles hacia `destino` («¿De dónde sale?»): «Listo para
 * asignar» si tiene dinero, luego las que tienen saldo (de más a menos) y
 * después el resto, también a 0 o en rojo (Vadik, 24-oct-2026: se puede
 * sacar de una categoría a 0 y dejarla en negativo), en el orden del Plan.
 * Fuera: ingresos, ocultas, borradas y el propio destino.
 */
export function origenesMover({
  grupos,
  saldos,
  toBudget,
  destino,
}: {
  grupos: readonly CategoryGroupEntity[];
  saldos: Saldos;
  toBudget: IntegerAmount | null | undefined;
  destino: string;
}): CandidataMover[] {
  const conSaldo: CandidataMover[] = [];
  const resto: CandidataMover[] = [];
  for (const grupo of grupos) {
    if (grupo.is_income || grupo.hidden || grupo.tombstone) {
      continue;
    }
    for (const c of grupo.categories ?? []) {
      if (c.id === destino || c.is_income || c.hidden || c.tombstone) {
        continue;
      }
      const saldo = saldos.get(c.id)?.balance ?? 0;
      const fila = { id: c.id, nombre: c.name, saldo, enRojo: saldo < 0 };
      (saldo > 0 ? conSaldo : resto).push(fila);
    }
  }
  conSaldo.sort((x, y) => y.saldo - x.saldo);
  const listo =
    destino !== LISTO_PARA_ASIGNAR && (toBudget ?? 0) > 0
      ? [
          {
            id: LISTO_PARA_ASIGNAR,
            nombre: null,
            saldo: toBudget ?? 0,
            enRojo: false,
          },
        ]
      : [];
  return [...listo, ...conSaldo, ...resto];
}

/** Importe al abrir: el Disponible del origen, nunca negativo. */
export function importePorDefecto(
  saldoOrigen: IntegerAmount | null | undefined,
): IntegerAmount {
  return Math.max(0, saldoOrigen ?? 0);
}

/** Atajo «La mitad»: la mitad del Disponible del origen, en céntimos. */
export function importeMitad(
  saldoOrigen: IntegerAmount | null | undefined,
): IntegerAmount {
  return Math.round(importePorDefecto(saldoOrigen) / 2);
}

/**
 * Importe escrito a mano, saneado: entero, nunca negativo y, si hay
 * `maximo` (el origen es «Listo para asignar», que no puede quedar en
 * negativo), sin pasar de él. `null` (no se entiende) → 0.
 */
export function limitarImporte(
  valor: number | null | undefined,
  maximo?: IntegerAmount | null,
): IntegerAmount {
  if (valor == null || !Number.isFinite(valor)) {
    return 0;
  }
  const entero = Math.max(0, Math.round(valor));
  return maximo == null ? entero : Math.min(entero, Math.max(0, maximo));
}

/** Movimiento listo para la acción de Actual. */
export type Movimiento = {
  desde: string;
  hacia: string;
  importe: IntegerAmount;
};

/**
 * Origen y destino según el modo de la hoja: en «desde» el fijo es el
 * origen; en «hacia» (invertido con ↑↓), el destino. Devuelve `null` si no
 * hay nada que mover (importe 0 o los dos extremos iguales).
 */
export function movimientoDe({
  modo,
  fijo,
  elegido,
  importe,
  toBudget,
}: {
  modo: 'desde' | 'hacia';
  fijo: string;
  elegido: string;
  importe: IntegerAmount;
  toBudget: IntegerAmount | null | undefined;
}): Movimiento | null {
  const desde = modo === 'desde' ? fijo : elegido;
  const hacia = modo === 'desde' ? elegido : fijo;
  const final = limitarImporte(
    importe,
    desde === LISTO_PARA_ASIGNAR ? (toBudget ?? 0) : null,
  );
  if (final <= 0 || desde === hacia) {
    return null;
  }
  return { desde, hacia, importe: final };
}

/**
 * Cifra que se propone cuando ya hay origen y destino: si el destino está
 * en rojo, lo que le falta (sin pasar de lo que tiene el origen, salvo que
 * el origen no tenga nada: entonces lo que falta entero); si no, todo el
 * Disponible del origen. Nunca negativo; 0 = que la escriba quien mueve.
 */
export function importeSugerido(
  saldoOrigen: IntegerAmount | null | undefined,
  saldoDestino: IntegerAmount | null | undefined,
): IntegerAmount {
  const todo = importePorDefecto(saldoOrigen);
  const destino = saldoDestino ?? 0;
  if (destino < 0) {
    return todo > 0 ? Math.min(-destino, todo) : -destino;
  }
  return todo;
}

/**
 * Monedas del montón de «Mover dinero»: más altas cuanto más Disponible
 * (escala logarítmica, de 1 a 6). A 0, en rojo o sin elegir, ninguna (se
 * ve el hueco).
 */
export function monedasDe(saldo: IntegerAmount | null | undefined): number {
  if (saldo == null || saldo <= 0) {
    return 0;
  }
  const euros = saldo / 100;
  return euros < 1 ? 1 : Math.min(6, 1 + Math.floor(Math.log10(euros) * 1.6));
}

/** Monedas que caen al mover: una, dos desde 10 € y tres desde 100 €. */
export function monedasQueCaen(importe: IntegerAmount): number {
  return 1 + (importe >= 10_00 ? 1 : 0) + (importe >= 100_00 ? 1 : 0);
}

/** Cuántas veces se ha movido desde / hacia cada categoría (en este móvil). */
export type UsadasMover = {
  desde?: Record<string, number>;
  hacia?: Record<string, number>;
};

/** Por qué una categoría sale en «Lo más probable». */
export type MotivoProbable = 'rojo' | 'sueles' | null;

export type SeccionesMover = {
  /** «★ Lo más probable»; vacío al buscar. */
  probables: Array<CandidataMover & { motivo: MotivoProbable }>;
  /** Grupos del Plan, en su orden, sin los vacíos. */
  grupos: Array<{ id: string; nombre: string; filas: CandidataMover[] }>;
  /** «Listo para asignar» cuando no va ya en «Lo más probable». */
  listo: CandidataMover | null;
};

/** Máximo de «sueles mover aquí» en «Lo más probable». */
const MAX_SUELES = 3;

/**
 * Lista de «Mover dinero» por secciones (Vadik, 25-oct-2026): arriba «Lo
 * más probable» (en «Hacia», las que están en rojo; en los dos lados, las
 * que más se usan y, en «Desde», «Listo para asignar» si tiene dinero) y
 * debajo todas por grupos del Plan, en su orden (las probables también,
 * para encontrarlas donde siempre). Con `busqueda`, solo las que la
 * contienen (sin tildes ni mayúsculas), sin «Lo más probable».
 */
export function seccionesMover({
  candidatas,
  grupos,
  lado,
  usadas,
  busqueda = '',
  nombreListo,
}: {
  candidatas: readonly CandidataMover[];
  grupos: readonly CategoryGroupEntity[];
  lado: 'desde' | 'hacia';
  usadas?: UsadasMover | null;
  busqueda?: string;
  /** «Listo para asignar» traducido, para buscarlo por su nombre. */
  nombreListo: string;
}): SeccionesMover {
  const buscado = getNormalisedString(busqueda.trim());
  const coincide = (c: CandidataMover) =>
    buscado === '' ||
    getNormalisedString(c.nombre ?? nombreListo).includes(buscado);
  const porId = new Map(candidatas.map(c => [c.id, c]));

  const filasGrupos: SeccionesMover['grupos'] = [];
  for (const grupo of grupos) {
    const filas = (grupo.categories ?? []).flatMap(c => {
      const candidata = porId.get(c.id);
      return candidata && coincide(candidata) ? [candidata] : [];
    });
    if (filas.length > 0) {
      filasGrupos.push({ id: grupo.id, nombre: grupo.name, filas });
    }
  }

  const probables: SeccionesMover['probables'] = [];
  if (buscado === '') {
    const ya = new Set<string>();
    const poner = (c: CandidataMover, motivo: MotivoProbable) => {
      if (!ya.has(c.id)) {
        ya.add(c.id);
        probables.push({ ...c, motivo });
      }
    };
    const listo = porId.get(LISTO_PARA_ASIGNAR);
    if (lado === 'desde' && listo && listo.saldo > 0) {
      poner(listo, null);
    }
    if (lado === 'hacia') {
      for (const { filas } of filasGrupos) {
        filas.filter(c => c.enRojo).forEach(c => poner(c, 'rojo'));
      }
    }
    Object.entries(usadas?.[lado] ?? {})
      .flatMap(([id, veces]) => {
        const c = porId.get(id);
        return c && veces > 0 ? [{ c, veces }] : [];
      })
      .sort((x, y) => y.veces - x.veces)
      .slice(0, MAX_SUELES)
      .forEach(({ c }) => poner(c, 'sueles'));
  }

  const listo = porId.get(LISTO_PARA_ASIGNAR);
  return {
    probables,
    grupos: filasGrupos,
    listo:
      listo && coincide(listo) && !probables.some(p => p.id === listo.id)
        ? listo
        : null,
  };
}

/** Máximo de categorías que se recuerdan por lado. */
const MAX_USADAS = 20;

/** `usadas` tras mover de `desde` a `hacia` (se quedan las más usadas). */
export function anotarUsada(
  usadas: UsadasMover | null | undefined,
  { desde, hacia }: { desde: string; hacia: string },
): UsadasMover {
  const sumar = (cuenta: Record<string, number> = {}, id: string) =>
    Object.fromEntries(
      Object.entries({ ...cuenta, [id]: (cuenta[id] ?? 0) + 1 })
        .sort(([, x], [, y]) => y - x)
        .slice(0, MAX_USADAS),
    );
  return {
    desde: sumar(usadas?.desde, desde),
    hacia: sumar(usadas?.hacia, hacia),
  };
}
